/**
 * The limited-capacity sessions on My Schedule that still have no seat.
 *
 * Drives the return-visit prompt (Figma 4669:57041), the first screen of the
 * "Seat Reservations Open" flow: an attendee who added limited-capacity
 * sessions before reservations opened comes back to find they still have to
 * claim a seat for each one.
 *
 * Pure (no Vue APIs) so it is safe in shared/ and unit-testable.
 *
 * @param sessions      the catalog
 * @param user          the auth session user (scheduled / sessionRegistration / sessionWaitlist)
 * @returns the sessions needing a seat, earliest first
 */
export function pendingSeatSessions(sessions, user) {
    const scheduled = new Set(user?.scheduled || []);
    if (scheduled.size === 0) return [];

    // Both lists hold BIZZABO session ids, so they are compared against the
    // reconciled bizzabo_session_id — never unique_id, which is a Sessionboard
    // friendly_id and appears in neither.
    const held = new Set([
        ...(user?.sessionRegistration || []),
        ...(user?.sessionWaitlist || []),
    ].map(String));

    return (sessions || [])
        .filter((s) => {
            if (!s || s.session_reservations !== true) return false;
            if (!scheduled.has(s.unique_id)) return false;
            // A session we could never reserve (no confident Bizzabo match)
            // must not be listed — the prompt would send the attendee to a
            // button that refuses. See reconcile.js on why the id can be null.
            if (!s.bizzabo_session_id) return false;
            return !held.has(String(s.bizzabo_session_id));
        })
        .slice()
        .sort((a, b) => new Date(a.start_date_and_time) - new Date(b.start_date_and_time));
}
